import { groupBy } from "lodash";
import type { LocalDishType, LocalFridgeSegment } from "types/mainTypes";

type IParsedPosition = {
	row: number;
	column: number;
	dishCount?: number;
};

/**
 * Groups segments sent from API into rows, so we can get shape of each row
 * example value [[false, false, false], [2, false]]
 *
 * The API returns segments in no particular order, so they are sorted by row and column first —
 * both rows and the cells within a row are rendered in array order.
 */
export const groupSegmentsByRows = (
	segments: IParsedPosition[],
	mealPositions: IParsedPosition[] | undefined,
): Array<boolean | number>[] => {
	const sortedSegments = [...segments].sort((a, b) => a.row - b.row || a.column - b.column);
	const rows = new Map<number, Array<boolean | number>>();

	for (const segment of sortedSegments) {
		const mealPosition = mealPositions?.find(
			(position) => position.row === segment.row && position.column === segment.column,
		);
		const cells = rows.get(segment.row) ?? [];

		cells.push(mealPosition?.dishCount ? mealPosition.dishCount : false);
		rows.set(segment.row, cells);
	}

	return [...rows.values()];
};

const convertPositionToRowAndColumn = (position: string, dishCount?: number) => {
	const segmentSections = position.split("_");
	const segmentRow = Number.parseInt(segmentSections[1], 10);
	const segmentColumn = Number.parseInt(segmentSections[2], 10);

	return {
		row: segmentRow,
		column: segmentColumn,
		dishCount: dishCount,
	};
};

/**
 * Parses information about fridge segments from API to a format that is easier to work with,
 * with the dishes of the given dish type counted into the cells they occupy.
 * example conversion:
 *      API response:
 *      {
 *          segments: [
 *              {segment: {type: "BOX_1_1"}},
 *              {segment: {type: "BOX_1_2"}},
 *              {segment: {type: "BOX_2_1"}}
 *          ]
 *      }
 *      returns: [[false, false], [false]]
 */
export const parseFridgeSegments = (fridgeSegments: LocalFridgeSegment[] | null, dishType: LocalDishType) => {
	if (!fridgeSegments) {
		return [];
	}
	const parsedSegments = fridgeSegments.flatMap((fridgeSegment) =>
		fridgeSegment?.segment?.type ? [convertPositionToRowAndColumn(fridgeSegment.segment.type)] : [],
	);

	const groupedDishes = groupBy(dishType.dishes, (dish) => dish.position);

	const parsedMealPositions = Object.keys(groupedDishes).map((dishPosition) =>
		convertPositionToRowAndColumn(dishPosition, groupedDishes[dishPosition].length),
	);

	return groupSegmentsByRows(parsedSegments, parsedMealPositions);
};
